// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterItemDetails.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import { MASKED_TEXT } from 'logic/data/Izenda.UIConstants';
import OverflowText from 'share/components/controls/Izenda.OverflowText';
import { observable, action } from 'mobx';
import { getFilterAliasValidation } from 'modules/ReportDesigner/Fields/Filter/Izenda.FilterPropertiesSchema';
import classnames from 'classnames';
import Input from 'share/components/controls/Izenda.Input';
import Validator from 'share/components/hoc/Izenda.Validator';
import actions from './Izenda.FilterItemActions';
import QuerySourceTypeConfig from 'logic/data/Izenda.QuerySourceTypeConfig';
const { TABLE, VIEW, STORED_PROC } = QuerySourceTypeConfig.QUERY_SOURCE_TYPE;
const ValidatorInput = Validator()(Input);
const VALIDATION_KEY = 'name';
const ENTER_KEY = 'Enter';


@connect(
  { toClass: true },
  actions
)
export default class FilterItemDetails extends PureComponent {
  static propTypes = {
    itemData: PropTypes.object.isRequired,
    store: PropTypes.object.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    visible: PropTypes.bool.isRequired,
    isMenuContextVisible: PropTypes.bool.isRequired,
    showPosition: PropTypes.bool.isRequired,
    enableEditMode: PropTypes.func.isRequired,
    disableEditMode: PropTypes.func.isRequired
  };

  // Current filter alias in the input
  @observable currentAlias: string = '';

  /**
   * Set current alias by filter item alias
   */
  setCurrentAlias() {
    const {
      itemData: { alias }
    } = this.props;
    this.currentAlias = alias;
  }

  /**
   * On change filter alias input
   */
  onChange = (e: Event) => {
    this.currentAlias = e.target.value;
  };

  /**
   * Enable edit mode
   */
  enableEditMode = (e: Event) => {
    this.setCurrentAlias();
    this.props.enableEditMode(e);
  };

  /**
   * On key press in input
   */
  onKeyPress = (e: Event) => {
    e.key == ENTER_KEY && this.onSave();
  };

  /**
   * On cancel change filter alias
   */
  onCancel = action(() => {
    this.setCurrentAlias();
    this.props.disableEditMode();
  });

  /**
   * On save change filter alias
   */
  onSave = action(() => {
    const { currentAlias, props } = this;
    const {
      itemData,
      validation,
      store: {
        reportStore: {
          model
        }
      }
    } = props;
    const oldAlias = itemData.alias;

    validation.validate(VALIDATION_KEY).then((success: boolean) => {
      if (success) {
        itemData.updateByKey('alias', currentAlias);

        // If this is key join filter - we need to update aliases in relationship key joins too
        if (itemData.isForKeyJoin) {
          model.updateRelationKeyJoinFilterAlias(oldAlias, currentAlias);
        }

        props.disableEditMode();
      }
    });
  });

  /**
   * Render position
   */
  renderFilterPosition() {
    const {
      showPosition,
      itemData: { isParameter, isForKeyJoin, position }
    } = this.props;

    return (
      showPosition && (
        <div className='filter-position'>
          {isForKeyJoin && <i className='icon-link'/>}
          {isParameter && <i className='icon-sp' />}
          {position}
        </div>
      )
    );
  }

  /**
   * Render filter alias
   */
  renderFilterAlias() {
    const {
      localization,
      itemData: { sourceDataObjectName, querySourceType },
      checkValidFilter
    } = this.props;
    const isValidFilter = checkValidFilter();
    const sourceNameForTooltip =
      querySourceType === TABLE || querySourceType === VIEW || querySourceType === STORED_PROC
        ? sourceDataObjectName
          ? ` [${sourceDataObjectName}]`
          : ''
        : '';
    return isValidFilter ? (
      this.renderEditableFilterAlias()
    ) : (
      <div>
        <OverflowText className='filter-alias' text={MASKED_TEXT} />
        <span
          className='icon-attention u_floatRight'
          title={String.format(localization.getText('REPORTDESIGNER_FILTER_TOOLTIP'), sourceNameForTooltip)}
        />
      </div>
    );
  }

  /**
   * Render editable filter alias
   */
  renderEditableFilterAlias() {
    const {
      itemData,
      itemData: { alias },
      localization,
      store,
      isEditMode
    } = this.props;

    const reportFilters = store.reportFilters || {};

    return isEditMode ? (
      <div className='title-edit form-group'>
        <div className='filter-alias-input'>
          <div>
            <ValidatorInput
              value={this.currentAlias}
              onChange={this.onChange}
              onKeyPress={this.onKeyPress}
              onBlur={this.onSave}
              field={VALIDATION_KEY}
              validations={getFilterAliasValidation(reportFilters, itemData)}
              className='form-control'
              autoFocus
            />
          </div>
          <span className='input-group-btn'>
            <button
              className='btn btn-default'
              title={localization.getText('COMMON_TOOLTIP_UPDATE_CHANGE')}
              onMouseDown={this.onSave}
            >
              <span className='icon-ok success-color' />
            </button>
            <button
              className='btn btn-default'
              title={localization.getText('COMMON_TOOLTIP_CANCEL_CHANGE')}
              onMouseDown={this.onCancel}
            >
              <span className='icon-cancel' />
            </button>
          </span>
        </div>
      </div>
    ) : (
      <div>
        <OverflowText className='filter-alias' text={alias} tooltip={alias} />
        <i
          className='icon-pencil selectable'
          title={localization.getText('COMMON_TOOLTIP_RENAME')}
          onClick={this.enableEditMode}
        />
      </div>
    );
  }

  render() {
    const {
      itemData,
      store: {
        reportFilters: { configField }
      },
      visible,
      isMenuContextVisible,
      isEditMode,
      className
    } = this.props;

    const classes = classnames(
      'filter-control',
      {
        'edit-mode': isEditMode,
        selected: itemData === configField.fieldDetails && (visible || isMenuContextVisible || isEditMode)
      },
      className
    );

    return (
      <div className={classes}>
        {this.renderFilterPosition()}
        {this.renderFilterAlias()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/Filter/Izenda.FilterItemDetails.js